import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resetErrorNetwork } from "../../utils/networkStatusBus";
import { probeNetwork } from "../../utils/networkProbe";
import { PageRoutes } from "../../constants/routes";
import { TypographyVariant } from "../../constants/typography";
import { Typography } from "../../components/core/Typography/Typography";
import { Button } from "../../components/core/Button/Button";
import { useGamepadContext } from "../../contexts/GamepadProvider";
import { useAppSelector } from "../../components/hooks";
import { selectIsMobile } from "../../store/slices/appState";
import { useStyles } from "./ErrorNetwork.style";

const TITLE = "Oops, Something Went Wrong";
const DESCRIPTION_LINE_1 = "We couldn’t complete your request.";
const DESCRIPTION_LINE_2 = "Please check your connection and try again.";
const TRY_AGAIN_LABEL = "Try Again";

type LocationState = { from?: string; fromState?: unknown } | null;

const ErrorNetwork = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [retrying, setRetrying] = useState(false);
  const { setCallback, cleanup, setActiveElement } = useGamepadContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useAppSelector(selectIsMobile);

  const locationState = location.state as LocationState;
  const from = locationState?.from || PageRoutes.Home;
  const fromState = locationState?.fromState;

  useEffect(() => {
    setCallback({
      onBackAction: () => {
        /* no-op: block Back navigation */
      },
    });
    return () => cleanup();
  }, [setCallback, cleanup]);

  useEffect(() => {
    if (retrying || !buttonRef.current) return;
    setActiveElement(buttonRef.current);
  }, [retrying, setActiveElement]);

  const handleTryAgain = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);

    const online = await probeNetwork();
    if (!online) {
      setRetrying(false);
      return;
    }

    resetErrorNetwork();
    navigate(from, { replace: true, state: fromState });
  }, [retrying, from, fromState, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const online = await probeNetwork();
      if (cancelled || !online) return;
      resetErrorNetwork();
      navigate(from, { replace: true, state: fromState });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.textBlock}>
          <Typography
            variant={
              isMobile
                ? TypographyVariant.HeadingH4Bold
                : TypographyVariant.HeadingH3Bold
            }
            className={classes.title}
          >
            {TITLE}
          </Typography>
          <Typography
            variant={
              isMobile
                ? TypographyVariant.BodyB5Regular
                : TypographyVariant.BodyB3Regular
            }
            className={classes.description}
          >
            {DESCRIPTION_LINE_1}
            <br />
            {DESCRIPTION_LINE_2}
          </Typography>
        </div>
        <Button
          ref={buttonRef}
          variant="contained"
          size="large"
          fullWidth={isMobile}
          loadingText={retrying ? TRY_AGAIN_LABEL : undefined}
          loadingIconSize={15}
          disabled={retrying}
          onClick={handleTryAgain}
          className={classes.button}
          data-gamepad="default-selected"
          data-testid="error-network-try-again"
        >
          {TRY_AGAIN_LABEL}
        </Button>
      </div>
    </div>
  );
};

export default ErrorNetwork;
